/**
 * escpos-types — Types partagés pour le pipeline d'impression VisionPOS.
 *
 * Ce fichier est la source de vérité des types utilisés par tous les modules
 * du dossier src/services/print/. Il ne doit jamais importer de Supabase,
 * React ni Capacitor (utilisé par des modules purs).
 *
 * Invariant 4.A.16 : les mentions NF525 (fiscal_hash, ticket_number, mention_nf525,
 * mention_agec) sont définies ici et doivent être présentes dans toute TicketData.
 */

// ── ESC/POS constants ────────────────────────────────────────────────────────

/** Byte d'échappement ESC (0x1B). */
export const ESC = '\x1B';
/** Group Separator GS (0x1D). */
export const GS = '\x1D';

/**
 * Séquence DLE DC4 — ouverture de tiroir-caisse via RJ12 externe (Géon) sur
 * imprimante AIDL Sunmi. La méthode AIDL openDrawer() ne fonctionne PAS pour
 * les tiroirs RJ12 (cf. project_printer_fix_20260411).
 */
export const DRAWER_KICK_AIDL = new Uint8Array([0x10, 0x14, 0x00, 0x00, 0x00]);

/**
 * Séquence ESC p — standard ESC/POS pour tiroirs branchés sur imprimantes
 * BT/Network génériques.
 */
export const DRAWER_KICK_ESCPOS = new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);

// ── PrinterConfig ────────────────────────────────────────────────────────────

/** Configuration d'une imprimante tenant (table tenant_printers). */
export interface PrinterConfig {
  id: string;
  name: string;
  connection_type: 'sunmi' | 'bluetooth' | 'network' | 'usb';
  printer_type: 'receipt' | 'kitchen' | 'bar' | 'label';
  printer_function?: string | null;
  bt_mac_address: string | null;
  ip_address: string | null;
  port: number;
  register_id: string | null;
  is_default: boolean;
  paper_width: number;
  char_per_line: number;
}

// ── TicketData ───────────────────────────────────────────────────────────────

/**
 * Structure complète d'un ticket de caisse NF525.
 *
 * INVARIANT 4.A.16 — les champs suivants sont OBLIGATOIRES sur tout ticket
 * fiscal (original ou duplicata) :
 *   - store_name + store_siret  (identité commerçant)
 *   - ticket_number             (PV-YYYY-NNNNNN séquentiel sans trou)
 *   - date + time               (horodatage émission)
 *   - tva_breakdown             (détail TVA par taux)
 *   - total_ht + total_ttc      (totaux)
 *   - fiscal_hash               (SHA-256 chaîné)
 *   - config.mention_nf525      (identifiant logiciel + version)
 *   - isDuplicate → bandeau DUPLICATA si réimpression
 */
export interface TicketData {
  /**
   * Réimpression d'un ticket déjà imprimé : si true, le ticket est précédé
   * d'un bandeau "*** DUPLICATA ***" en gros caractères centré (conformité
   * NF525 art. 286 I-3°bis CGI — un document fiscal réimprimé doit être
   * clairement identifié comme copie).
   */
  isDuplicate?: boolean;
  // Header
  store_name: string;
  store_subtitle?: string;
  store_address?: string;
  store_phone?: string;
  store_email?: string;
  store_siret?: string;
  tva_intracom?: string;
  // Meta
  ticket_number?: string;
  date: string;
  time: string;
  cashier: string;
  register: string;
  client?: string;
  // Items
  items: Array<{
    name: string;
    quantity: number;
    unit_price: number;
    tva_rate: number;
    ref?: string;
  }>;
  /** Remise globale de la vente en % (sales.discount_percent) — lignes Sous-total / Remise sur le ticket. */
  discount_percent?: number;
  // Totals
  total_ttc: number;
  total_ht: number;
  tva_breakdown: Array<{ rate: number; ht: number; tva: number }>;
  // Payment
  payment_method: string;
  /** Détail d'un paiement fractionné/multiple (moyens + montants). Clés brutes,
   * libellées à l'affichage via formatPaymentLabel. */
  split_payments?: Array<{ method: string; amount: number }>;
  card_scheme?: string;
  pan_masked?: string;
  auth_code?: string;
  aid?: string;
  entry_mode?: string;
  tid?: string;
  mid?: string;
  /** Référence de rapprochement pour TPE externe (RRN ou équivalent). */
  rrn?: string;
  /** Ligne carte unique (réglage retour_tpe_detaille) ; présente = remplace le bloc carte historique. */
  card_line?: string;
  // Fiscal
  fiscal_hash?: string;
  // Config flags (from ticket_config)
  config: {
    show_date: boolean;
    show_heure: boolean;
    show_vendeur: boolean;
    show_num_ticket: boolean;
    show_client: boolean;
    show_ref_article: boolean;
    show_detail_tva: boolean;
    show_tva_par_taux: boolean;
    show_mode_reglement: boolean;
    /** Ligne carte sur le ticket (défaut true) — n'agit que si retour_tpe_detaille est actif. */
    show_ligne_carte?: boolean;
    show_hash_fiscal: boolean;
    show_merci: boolean;
    show_code_barres_ticket: boolean;
    show_qr_code: boolean;
    merci_text: string;
    footer_text: string;
    footer_legal: string;
    /** Mention identifiant logiciel + version (NF525 obligatoire). */
    mention_nf525: string;
    /** Mention AGEC obligatoire : "Ticket imprimé à votre demande — Loi du 10 fév 2020 art. 49" */
    mention_agec?: string;
    qr_code_content: string;
    police_size: number;
    largeur_papier: number;
    [key: string]: unknown;
  };
}
